import React from 'react';
import { Box, Tab, MenuItem, TextField } from '@mui/material';
import TabList from '@mui/lab/TabList';
import { tabStyle } from '../constants/testingConfigs';
import TestingSheetStrategy from '../strategies/TestingSheetStrategy';

/**
 * TestingTabNavigation Component
 * Renders the tab navigation with TabList and dropdown selector
 * Handles tab styling based on test results
 *
 * @param {Object} RCMData - The RCM data
 * @param {string} type - The template type
 * @param {string} testingSheetType - The testing sheet type
 * @param {string} value - The current tab value
 * @param {Function} setValue - Callback to set the active tab
 */
const TestingTabNavigation = ({ RCMData, type, value, setValue }) => {
  if (!RCMData) return null;

  const shouldRenderMasterTableSheets =
    TestingSheetStrategy.shouldRenderMasterTableSheets(type);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <TabList
          aria-label="lab API tabs example"
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
        >
          {/* Main tabs for each table */}
          {Object.keys(RCMData['rows']).map((table, idx) => {
            return (
              <Tab
                key={`Tab Number ${idx + 1}`}
                style={tabStyle}
                label={table}
                value={`${idx + 1}`}
                onClick={() => {
                  setValue(`${idx + 1}`);
                }}
              />
            );
          })}

          {/* Individual sheet tabs for Master Table rows */}
          {shouldRenderMasterTableSheets &&
            RCMData.rows['Master Table'].map((row, idx) => {
              const overallConclusionField =
                TestingSheetStrategy.getOverallConclusionField(type);
              const overallConclusion =
                RCMData['rows']['Master Table']?.[idx]?.[
                  overallConclusionField
                ];
              const dynamicColorStyle =
                TestingSheetStrategy.getTabColorStyle(overallConclusion);

              return (
                <Tab
                  key={`Tab Number ${idx + 2}`}
                  style={dynamicColorStyle}
                  label={`${idx + 1}`}
                  value={`${idx + 2}`}
                  onClick={() => {
                    setValue(`${idx + 2}`);
                  }}
                />
              );
            })}
        </TabList>
      </Box>

      {/* Dropdown selector */}
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '10px 0px 10px'
        }}
      >
        <TextField
          variant="standard"
          size="small"
          value={value}
          style={{
            backgroundColor: 'white',
            color: 'rgba(0, 0, 0, 0.38)',
            outline: 'none',
            borderRadius: '2px',
            marginRight: '10px',
            fontSize: '15px',
            padding: '5px',
            minWidth: '50px',
            textAlign: 'center'
          }}
          select
        >
          {/* Main table dropdown items */}
          {Object.keys(RCMData.rows).map((table, idx) => {
            return (
              <MenuItem
                key={`dropdown item number ${idx + 1}`}
                value={`${idx + 1}`}
                onClick={() => {
                  setValue(`${idx + 1}`);
                }}
              >
                {table}
              </MenuItem>
            );
          })}

          {/* Individual sheet dropdown items */}
          {shouldRenderMasterTableSheets &&
            RCMData.rows['Master Table'].map((row, index) => {
              return (
                <MenuItem
                  key={`dropdown item number ${index}`}
                  value={`${index + 2}`}
                  onClick={() => {
                    setValue(`${index + 2}`);
                  }}
                >
                  Sheet {index + 1}
                </MenuItem>
              );
            })}
        </TextField>
      </Box>
    </>
  );
};

export default TestingTabNavigation;
